import { analyzeIronPythonSemantics } from './ironPythonSemantics';

export type IronPythonDiagnosticSeverity = 'error' | 'warning';

export interface IronPythonDiagnostic {
  line: number;
  column?: number;
  message: string;
  severity: IronPythonDiagnosticSeverity;
  source?: 'local' | 'ironpython';
}

export interface IronPythonAnalysis {
  diagnostics: IronPythonDiagnostic[];
  errorCount: number;
  warningCount: number;
}

export type IronPythonTokenKind = 'plain' | 'comment' | 'keyword' | 'number' | 'string' | 'symbol';

export interface IronPythonToken {
  kind: IronPythonTokenKind;
  value: string;
}

const IronPythonKeywords = new Set([
  'and', 'as', 'assert', 'async', 'await', 'break', 'class', 'continue', 'def', 'del',
  'elif', 'else', 'except', 'False', 'finally', 'for', 'from', 'global', 'if', 'import',
  'in', 'is', 'lambda', 'None', 'nonlocal', 'not', 'or', 'pass', 'raise', 'return',
  'True', 'try', 'while', 'with', 'yield',
]);

const StructuralSymbols = new Set(['(', ')', '[', ']', '{', '}', ':', ',']);
const OpeningDelimiters: Record<string, string> = { '(': ')', '[': ']', '{': '}' };
const ClosingDelimiters: Record<string, string> = { ')': '(', ']': '[', '}': '{' };
const DelimiterNames: Record<string, string> = {
  '(': 'parenthesis',
  '[': 'bracket',
  '{': 'brace',
};

export const MaxIronPythonScriptLength = 131_072;

const addDiagnostic = (
  diagnostics: IronPythonDiagnostic[],
  diagnostic: IronPythonDiagnostic
) => {
  if (!diagnostics.some((item) => (
    item.line === diagnostic.line
    && item.severity === diagnostic.severity
    && item.message === diagnostic.message
  ))) {
    diagnostics.push(diagnostic);
  }
};

const checkEntryPoint = (lines: string[], diagnostics: IronPythonDiagnostic[]) => {
  const candidateLine = lines.findIndex((line) => /^\s*def\s+get_elem_property\b/.test(line));

  if (candidateLine < 0) {
    addDiagnostic(diagnostics, {
      line: 1,
      severity: 'error',
      message: 'Define get_elem_property(elem) as the PipeEX entry point.',
    });
    return;
  }

  if (!/^\s*def\s+get_elem_property\s*\(\s*elem\s*\)\s*:\s*(?:#.*)?$/.test(lines[candidateLine])) {
    addDiagnostic(diagnostics, {
      line: candidateLine + 1,
      severity: 'error',
      message: 'Use def get_elem_property(elem): with the required trailing colon.',
    });
  }
};

interface OpenDelimiter {
  character: string;
  line: number;
}

const reportUnclosedStructures = (
  stack: OpenDelimiter[],
  quote: 'single' | 'double' | 'triple-single' | 'triple-double' | null,
  quoteLine: number,
  diagnostics: IronPythonDiagnostic[]
) => {
  if (quote) {
    addDiagnostic(diagnostics, {
      line: quoteLine,
      severity: 'error',
      message: 'Close the unterminated string literal.',
    });
  }

  stack.forEach((opening) => {
    addDiagnostic(diagnostics, {
      line: opening.line,
      severity: 'error',
      message: `Close the opening ${DelimiterNames[opening.character]}.`,
    });
  });
};

const checkStructure = (source: string, diagnostics: IronPythonDiagnostic[]) => {
  const stack: OpenDelimiter[] = [];
  let line = 1;
  let quote: 'single' | 'double' | 'triple-single' | 'triple-double' | null = null;
  let quoteLine = 1;
  let escaped = false;
  let inComment = false;

  for (let index = 0; index < source.length; index += 1) {
    const character = source[index];
    const triple = source.slice(index, index + 3);

    if (character === '\n') {
      if (quote === 'single' || quote === 'double') {
        addDiagnostic(diagnostics, {
          line: quoteLine,
          severity: 'error',
          message: 'Close the string literal before the end of the line.',
        });
        quote = null;
      }
      line += 1;
      escaped = false;
      inComment = false;
      continue;
    }

    if (inComment) {
      continue;
    }

    if (quote) {
      if (quote.startsWith('triple')) {
        const closingTriple = quote === 'triple-single' ? "'''" : '"""';
        if (triple === closingTriple) {
          quote = null;
          index += 2;
        }
        continue;
      }

      if (escaped) {
        escaped = false;
        continue;
      }
      if (character === '\\') {
        escaped = true;
        continue;
      }
      if ((quote === 'single' && character === "'") || (quote === 'double' && character === '"')) {
        quote = null;
      }
      continue;
    }

    if (character === '#') {
      inComment = true;
      continue;
    }
    if (triple === "'''" || triple === '"""') {
      quote = triple === "'''" ? 'triple-single' : 'triple-double';
      quoteLine = line;
      index += 2;
      continue;
    }
    if (character === "'" || character === '"') {
      quote = character === "'" ? 'single' : 'double';
      quoteLine = line;
      continue;
    }
    if (OpeningDelimiters[character]) {
      stack.push({ character, line });
      continue;
    }
    if (ClosingDelimiters[character]) {
      const expectedOpening = ClosingDelimiters[character];
      const opening = stack.pop();
      if (!opening || opening.character !== expectedOpening) {
        addDiagnostic(diagnostics, {
          line,
          severity: 'error',
          message: `Remove or match the unexpected ${character}.`,
        });
      }
    }
  }

  reportUnclosedStructures(stack, quote, quoteLine, diagnostics);
};

const checkIndentation = (lines: string[], diagnostics: IronPythonDiagnostic[]) => {
  lines.forEach((line, index) => {
    const indentation = line.match(/^[ \t]*/)?.[0] ?? '';
    if (indentation.includes('\t')) {
      addDiagnostic(diagnostics, {
        line: index + 1,
        severity: 'warning',
        message: 'Replace indentation tabs with four spaces for predictable IronPython execution.',
      });
      return;
    }

    if (line.trim().length > 0 && indentation.length % 4 !== 0) {
      addDiagnostic(diagnostics, {
        line: index + 1,
        severity: 'warning',
        message: 'Use indentation in multiples of four spaces.',
      });
    }
  });
};

export const analyzeIronPython = (source: string): IronPythonAnalysis => {
  const diagnostics: IronPythonDiagnostic[] = [];
  const lines = source.split(/\r\n|\r|\n/);

  checkEntryPoint(lines, diagnostics);
  checkIndentation(lines, diagnostics);
  checkStructure(source, diagnostics);
  analyzeIronPythonSemantics(lines, diagnostics);
  if (source.length > MaxIronPythonScriptLength) {
    addDiagnostic(diagnostics, {
      line: 1,
      severity: 'error',
      message: `Keep the script within ${MaxIronPythonScriptLength} characters.`,
    });
  }

  diagnostics.sort((left, right) => left.line - right.line || left.severity.localeCompare(right.severity));

  return {
    diagnostics,
    errorCount: diagnostics.filter(({ severity }) => severity === 'error').length,
    warningCount: diagnostics.filter(({ severity }) => severity === 'warning').length,
  };
};

export const tokenizeIronPython = (source: string): IronPythonToken[] => {
  const tokenPattern = /(#[^\n]*|"(?:\\.|[^"\\])*"?|'(?:\\.|[^'\\])*'?|\b\d+(?:\.\d+)?\b|\b[A-Za-z_]\w*\b|[()[\]{}:,])/g;
  const tokens: IronPythonToken[] = [];
  let cursor = 0;

  for (const match of source.matchAll(tokenPattern)) {
    const index = match.index ?? 0;
    if (index > cursor) {
      tokens.push({ kind: 'plain', value: source.slice(cursor, index) });
    }

    const value = match[0];
    let kind: IronPythonTokenKind = 'plain';
    if (value.startsWith('#')) {
      kind = 'comment';
    } else if (value.startsWith('"') || value.startsWith("'")) {
      kind = 'string';
    } else if (/^\d/.test(value)) {
      kind = 'number';
    } else if (IronPythonKeywords.has(value)) {
      kind = 'keyword';
    } else if (StructuralSymbols.has(value)) {
      kind = 'symbol';
    }
    tokens.push({ kind, value });
    cursor = index + value.length;
  }

  if (cursor < source.length) {
    tokens.push({ kind: 'plain', value: source.slice(cursor) });
  }

  return tokens;
};
